import _ from 'lodash';
import React, {FC, Fragment, ReactNode} from 'react';

import {Trans} from '../../libs/shared/core/src/helpers/internationalization/react';

/*
 * Props.
 */

export interface ItemsFormatterItem {
  id: string | number;
  name: ReactNode;
}

interface ItemsFormatterProps {
  /** The items to render. */
  items: ReadonlyArray<ItemsFormatterItem>;
  /** The maximum number of names to display. */
  limit?: number;
  /** Render name. */
  renderName?: NameDecorator;
  /** Render "X others" text. */
  renderOthersText?: OthersTextDecorator;
  /** Render the conjunction. Defaults to "and". */
  renderConjunction?: ConjunctionRenderer;
}

export type NameDecorator = (name: ReactNode, id: string | number) => ReactNode;
export type OthersTextDecorator = (
  othersText: ReactNode,
  othersNames: ReactNode,
  othersCount: number,
) => ReactNode;

export type ConjunctionRenderer = (visibleNames: ReactNode, otherNames: ReactNode) => ReactNode;

/*
 * Constants.
 */

const defaultConjunctionRenderer: ConjunctionRenderer = (visibleNames, otherNames) => (
  <Trans id="names-many">
    {visibleNames} and {otherNames}
  </Trans>
);

/*
 * Component.
 */

export const ItemsFormatter: FC<ItemsFormatterProps> = (props) => {
  const {items} = props;
  const wrapper = props.renderName || _.identity;

  switch (items.length) {
    // If no names were provided, nothing to render.
    case 0:
      return null;

    // If we have only one name, render it.
    case 1:
      return <>{wrapper(items[0].name, items[0].id)}</>;

    // Otherwise, more complicated logic.
    default:
      return <>{renderManyNames(props)}</>;
  }
};

type ItemsProps = Pick<ItemsFormatterProps, 'items' | 'renderName'>;

/** Render a list of names, optionally decorating them. */
const Items: FC<ItemsProps> = (props) => {
  const {items} = props;
  const wrapper = props.renderName || _.identity;

  return (
    <>
      {items.map((item, index, array) => {
        const isNotBeforeLast = index < array.length - 1;

        return (
          <Fragment key={item.id}>
            {wrapper(item.name, item.id)}
            {isNotBeforeLast && ', '}
          </Fragment>
        );
      })}
    </>
  );
};

function renderManyNames(props: ItemsFormatterProps) {
  const {items, renderName} = props;
  const limit = props.limit || 2;

  const renderConjunction = props.renderConjunction ?? defaultConjunctionRenderer;

  // If we're not above the limit, just render all the names.
  if (items.length <= limit) {
    return renderConjunction(
      <Items items={items.slice(0, -1)} renderName={renderName} />,
      <Items items={items.slice(-1)} renderName={renderName} />,
    );
  }

  // Otherwise, render some names and a count.
  const actualLimit = limit - 1;
  const visibleNames = items.slice(0, actualLimit);
  const otherNames = items.slice(actualLimit);

  const renderedVisibleNames = <Items items={visibleNames} renderName={renderName} />;
  const renderedOtherNames = renderOtherNames(props, otherNames);

  return renderConjunction(renderedVisibleNames, renderedOtherNames);
}

/*
 * Helpers.
 */

function renderOtherNames(props: ItemsFormatterProps, otherNames: ReadonlyArray<ItemsFormatterItem>) {
  const wrapper = props.renderOthersText || _.identity;
  return wrapper(
    <Trans id="names-others">{otherNames.length} others</Trans>,
    <Items items={otherNames} />,
    otherNames.length,
  );
}
